import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCheck, FiChevronDown, FiSearch } from 'react-icons/fi';
import { getFlagOptions, getFlagUrl } from '../utilities/flags';
import { useI18n } from '../utilities/i18n';

function FlagImg({ code, className }) {
  const [failed, setFailed] = useState(false);
  const src = getFlagUrl(code);

  useEffect(() => {
    setFailed(false);
  }, [code]);

  if (!src || failed) return null;

  return (
    <img
      src={src}
      alt=""
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

export { FlagImg };

const MENU_MAX = 280;
const MENU_MAX_MOBILE = 264;

export default function FlagSelect({
  value,
  onChange,
  error,
  disabled,
}) {
  const { t, lang } = useI18n();
  const options = useMemo(() => getFlagOptions(lang), [lang]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const normalizedValue = String(value || '').trim().toLowerCase();
  const selected = options.find((item) => item.code === normalizedValue)
    || (normalizedValue ? { code: normalizedValue, name: normalizedValue.toUpperCase() } : null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((item) => (
      item.name.toLowerCase().includes(q) || item.code.toLowerCase().includes(q)
    ));
  }, [options, query]);

  const updateMenuPosition = () => {
    const trigger = rootRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const isMobile = typeof window !== 'undefined'
      && window.matchMedia('(max-width: 900px)').matches;
    const maxMenu = isMobile ? MENU_MAX_MOBILE : MENU_MAX;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 12;
    const spaceAbove = rect.top - gap - 12;
    const openUp = spaceBelow < Math.min(maxMenu, 160) && spaceAbove > spaceBelow;
    const available = Math.max(120, openUp ? spaceAbove : spaceBelow);
    const height = Math.min(maxMenu, available);

    setMenuStyle({
      position: 'fixed',
      left: Math.max(8, rect.left),
      width: Math.min(rect.width, window.innerWidth - 16),
      maxHeight: height,
      zIndex: 1200,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap, top: 'auto' }
        : { top: rect.bottom + gap, bottom: 'auto' }),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }

    const handleClickOutside = (event) => {
      const target = event.target;
      if (rootRef.current && rootRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    if (searchRef.current) searchRef.current.focus();

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const menu = open && menuStyle ? ReactDOM.createPortal(
    (
      <div className="tr-flag-menu is-portal" ref={menuRef} style={menuStyle}>
        <div className="tr-flag-search">
          <FiSearch />
          <input
            ref={searchRef}
            type="text"
            className="tr-flag-search-input"
            value={query}
            placeholder={t('trFlagSearch')}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className="tr-flag-list" role="listbox">
          {filtered.length ? filtered.map((item) => {
            const isSelected = item.code === normalizedValue;
            return (
              <button
                type="button"
                key={item.code}
                className={`tr-flag-option ${isSelected ? 'is-selected' : ''}`}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(item.code);
                  setOpen(false);
                }}
              >
                <FlagImg code={item.code} className="tr-flag-img" />
                <span className="tr-flag-label">{item.name}</span>
                <span className="tr-flag-code">{item.code}</span>
                {isSelected ? <FiCheck className="tr-flag-check" /> : null}
              </button>
            );
          }) : (
            <div className="tr-flag-empty">{t('trFlagEmpty')}</div>
          )}
        </div>
      </div>
    ),
    document.body,
  ) : null;

  return (
    <div className={`tr-flag-select ${error ? 'is-error' : ''} ${open ? 'is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="tr-flag-trigger"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className="tr-flag-current">
            <FlagImg code={selected.code} className="tr-flag-img" />
            <span className="tr-flag-label">{selected.name}</span>
            <span className="tr-flag-code">{selected.code}</span>
          </span>
        ) : (
          <span className="tr-flag-placeholder">{t('trFlagPlaceholder')}</span>
        )}
        <FiChevronDown />
      </button>
      {menu}
    </div>
  );
}
